/* HK header behaviour — ported from happikiddo-storefront/assets/js/main.js.
   Covers: mobile nav toggle, sticky header border on scroll, shared mega-panel,
   Horizon cart-drawer open + live cart count. Loaded as a module so the cart
   count can resolve @shopify/events through the theme importmap.
   NOTE: the import must be STATIC — Shopify's asset minifier treats a file
   with no top-level import/export as CommonJS and rewrites dynamic import()
   into require(), which does not exist in the browser. */
import { StandardEvents } from '@shopify/events';

(function () {
  'use strict';

  var header = document.getElementById('site-header');
  var burger = document.getElementById('nav-burger');

  /* Mobile nav toggle. The drawer is position: fixed and sits under the
     header row, so it needs to know the row's real height, and the page
     behind it must stop scrolling while it is open. */
  if (header && burger) {
    var setDrawerTop = function () {
      var main = header.querySelector('.header-main');
      if (main) {
        header.style.setProperty('--hk-header-h', main.getBoundingClientRect().bottom + 'px');
      }
    };
    burger.addEventListener('click', function () {
      var open = header.classList.toggle('nav-open');
      if (open) setDrawerTop();
      document.documentElement.classList.toggle('hk-nav-lock', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    });
    window.addEventListener('resize', function () {
      if (header.classList.contains('nav-open')) setDrawerTop();
    });
  }

  /* Mobile search toggle: the icon next to the cart expands the header's own
     search bar as a second row and focuses it. Escape or a tap outside the
     header closes it; opening it closes the nav drawer. */
  var searchToggle = document.getElementById('hk-search-toggle');
  var headerSearch = document.getElementById('hk-header-search');
  if (header && searchToggle && headerSearch) {
    var setSearchOpen = function (open) {
      header.classList.toggle('hk-search-open', open);
      searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        var input = headerSearch.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    };
    searchToggle.addEventListener('click', function () {
      var open = !header.classList.contains('hk-search-open');
      if (open && header.classList.contains('nav-open') && burger) burger.click();
      setSearchOpen(open);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setSearchOpen(false);
    });
    document.addEventListener('click', function (e) {
      if (!header.classList.contains('hk-search-open')) return;
      if (header.contains(e.target)) return;
      setSearchOpen(false);
    });
  }

  /* Mobile nav accordion: on phones the mega panel never renders, so a top
     row with children expands its sub-list instead of navigating. The parent
     collection is still reachable through the "Shop all X" row inside. */
  var mobileNav = window.matchMedia('(max-width: 960px)');
  if (header) {
    header.addEventListener('click', function (e) {
      if (!mobileNav.matches) return;
      var link = e.target.closest('.nav-link[data-hk-mega]');
      if (!link || !header.contains(link)) return;
      var sub = link.parentNode.querySelector(
        '.nav-sub[data-nav-sub="' + link.getAttribute('data-hk-mega') + '"]'
      );
      if (!sub) return;
      e.preventDefault();
      var open = sub.classList.toggle('is-open');
      link.classList.toggle('is-sub-open', open);
    });
  }

  /* Sticky header: subtle border once the page scrolls, no shadow.
     Horizon scrolls .page-wrapper (overflow-y: auto) on desktop and the
     window on mobile, so listen on both and read whichever moved. */
  if (header) {
    var scroller = header.closest('.page-wrapper');
    if (!scroller) {
      var el = header.parentElement;
      while (el && el !== document.documentElement) {
        var st = window.getComputedStyle(el);
        if (/(auto|scroll|overlay)/.test(st.overflowY)) { scroller = el; break; }
        el = el.parentElement;
      }
    }
    var scrollTop = function () {
      var winY = window.scrollY || document.documentElement.scrollTop || 0;
      var elY = scroller ? scroller.scrollTop : 0;
      return Math.max(winY, elY);
    };
    /* Mobile hide-on-scroll (Gymshark pattern, Sean 07/08): scrolling down
       past the header slides it away so the whole viewport is product;
       any scroll up brings it straight back. Desktop keeps plain sticky.
       An 8px deadband ignores rubber-banding jitter, and the header never
       hides while the drawer is open. */
    var mobileHeader = window.matchMedia('(max-width: 960px)');
    var lastY = scrollTop();
    var onScroll = function () {
      var y = scrollTop();
      header.classList.toggle('is-scrolled', y > 2);
      if (mobileHeader.matches && !header.classList.contains('nav-open')) {
        var dy = y - lastY;
        if (Math.abs(dy) > 8) {
          header.classList.toggle('is-condensed', dy > 0 && y > 140);
          lastY = y;
        }
      } else {
        header.classList.remove('is-condensed');
        lastY = y;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    if (scroller) scroller.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* Desktop mega-menu: one shared panel docked under the nav row inside the
     sticky header; content swaps per top-level link from Liquid-rendered
     templates. Hover intent + focus open, 140ms close delay, Escape closes. */
  var nav = document.getElementById('site-nav');
  if (nav && header) {
    var panel = document.createElement('div');
    panel.className = 'mega-panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', 'Category menu');
    header.appendChild(panel);

    var closeTimer = null;

    var openMega = function (key) {
      var tpl = header.querySelector('template[data-hk-mega-panel="' + key + '"]');
      if (!tpl) return;
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      panel.innerHTML = '';
      panel.appendChild(tpl.content.cloneNode(true));
      panel.classList.add('is-open');
    };
    var scheduleClose = function () {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { panel.classList.remove('is-open'); }, 140);
    };

    var links = nav.querySelectorAll('.nav-link[data-hk-mega]');
    for (var i = 0; i < links.length; i++) {
      (function (link) {
        var key = link.getAttribute('data-hk-mega');
        link.addEventListener('mouseenter', function () { openMega(key); });
        link.addEventListener('focus', function () { openMega(key); });
        link.addEventListener('mouseleave', scheduleClose);
      })(links[i]);
    }
    panel.addEventListener('mouseenter', function () { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } });
    panel.addEventListener('mouseleave', scheduleClose);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') panel.classList.remove('is-open');
    });
  }

  /* Cart: open Horizon's drawer (theme-drawer#cart-drawer, rendered from
     layout/theme.liquid) instead of navigating. Falls back to /cart when the
     drawer is absent (cart_type = page, or /cart template). */
  var cartBtn = document.querySelector('.hk-scope .header-action-cart');
  if (cartBtn) {
    cartBtn.addEventListener('click', function (e) {
      var drawer = document.getElementById('cart-drawer');
      if (!drawer) return; /* no drawer on this page: follow the link */
      if (window.customElements && customElements.upgrade) customElements.upgrade(drawer);
      if (typeof drawer.toggle !== 'function') return; /* module not ready: follow the link */
      e.preventDefault();
      drawer.toggle();
    });

    /* Live count: same event Horizon's cart-icon.js listens to
       (StandardEvents.cartLinesUpdate from @shopify/events; the event carries
       a promise resolving to { cart, detail }). Dynamic import so a CDN
       failure only loses the live count, not the rest of the header. */
    var label = cartBtn.querySelector('.header-action-label');
    if (label) {
      document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
        if (!event.promise) return;
        event.promise
          .then(function (result) {
            var cart = result && result.cart;
            var detail = result && result.detail;
            var count = cart && cart.totalQuantity != null
              ? cart.totalQuantity
              : detail && detail.itemCount != null ? detail.itemCount : null;
            if (count != null) label.textContent = 'Cart (' + count + ')';
          })
          .catch(function (error) {
            if (!error || error.name !== 'AbortError') console.warn('[hk-header] cart update promise rejected:', error);
          });
      });
    }
  }
})();
